import React from "react";
import { ArrowLeft } from "lucide-react";
import { BagLogo } from "../components/Logo";

const S = ({ n, title, children }) => (
  <section className="mt-6" data-testid={`terms-section-${n}`}>
    <h2 className="font-display font-black text-lg text-slate-900">{n}. {title}</h2>
    <div className="mt-2 space-y-2 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{children}</div>
  </section>
);

export default function LegalTermsEn() {
  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <div className="max-w-2xl mx-auto bg-white min-h-screen shadow-2xl px-6 py-8" data-testid="terms-page-en">
        <div className="flex items-center gap-3">
          <button data-testid="legal-back" onClick={() => (window.history.length > 1 ? window.history.back() : window.location.assign("/en"))} className="bg-slate-100 rounded-full p-2 active:scale-95 transition-transform"><ArrowLeft size={18} /></button>
          <a href="/en" className="flex items-center gap-2 text-brand-red font-display font-bold"><BagLogo size={28} /> Ponesi</a>
        </div>
        <h1 className="font-display font-black text-2xl sm:text-3xl text-slate-900 mt-5">Ponesi Terms of Use</h1>
        <p className="text-xs text-slate-400 mt-1">Version 1.0</p>

        <S n="1" title="COMPANY INFORMATION">{`The Ponesi internet platform, available at ponesi.rs, is operated by:

Sahara Group DOO
Masarikov Trg 18
16000 Leskovac
Republic of Serbia
Tax ID (PIB): 115572837
Company Registration Number: 22177460
Email: privacy@ponesi.rs

Hereinafter referred to as “Ponesi”.

These Terms of Use govern the rights and obligations of users when using the Ponesi platform.

By creating a user account and accepting these Terms, the user confirms that they have read, understood and accepted them.`}</S>

        <S n="2" title="ABOUT PONESI">{`Ponesi is a digital platform that allows users to find restaurants and other food-service businesses, view their offers, place orders and personally pick up ordered food or other products from the selected establishment.

Ponesi is not a delivery service.

Unless expressly stated otherwise, Ponesi does not prepare food and is not the manufacturer of products offered by restaurants.

Ponesi provides the digital connection between users and restaurants and enables the technical processing of orders.

The restaurant is responsible for food preparation, product quality, food safety, information about ingredients and allergens, product availability, issuing fiscal documentation where applicable and fulfilling accepted orders in accordance with applicable law.`}</S>

        <S n="3" title="USER ACCOUNT">{`Certain functions of Ponesi may require the user to create a user account.

The user is responsible for the accuracy of information they provide and for maintaining the security of access to their account.

Ponesi may require email verification before allowing a user to place an order.

Users may not use another person's account without authorization.`}</S>

        <S n="4" title="PLACING AN ORDER">{`The user selects a restaurant, products, product options, quantities and desired pickup time.

By submitting an order, the user sends the restaurant a request to prepare the selected order.

An order is considered accepted when the restaurant confirms it through the Ponesi system.

Until the restaurant responds, the customer will be shown the following notice:

“Please stay on this page until the restaurant accepts your order. Thank you.”

The restaurant may:
- accept the requested pickup time;
- propose a different pickup time;
- reject the order.`}</S>

        <S n="5" title="PICKUP TIME CHANGES">{`If the restaurant proposes another pickup time, the user is given a limited period, currently 60 seconds, to accept the proposed time or cancel the order.

The deadline is calculated using Ponesi server time and not the time configured on the user's device.

If the user does not respond within the specified period, the system may automatically cancel the order according to the rule displayed to the user during the ordering process.`}</S>

        <S n="6" title="ORDER PICKUP">{`Ponesi is intended for personal pickup of orders from the selected restaurant.

Each order may be assigned a public order number in a format such as:

AB7

The same public order number is shown to both the customer and the restaurant to make order identification easier.

The public order number is not a security token, and knowing the number alone does not provide access to the user's account or order information.`}</S>

        <S n="7" title="PRICES">{`Product prices are determined by restaurants.

The total price is displayed to the user before final order confirmation.

If the price of a product or selected option changes before final order creation, Ponesi may require the user to confirm the updated price.

The valid price at the time the order is created is determined using current server-side Ponesi data.

Price information stored only in the customer's browser is not authoritative.`}</S>

        <S n="8" title="PAYMENT">{`Until Ponesi introduces another payment system, payment is made directly at the restaurant when the order is collected, using a payment method supported by the relevant restaurant.

This may include cash or card payment at the restaurant.

If online payment is introduced in the future, additional terms and information relating to such payment will be provided to the user.`}</S>

        <S n="9" title="PRODUCT AVAILABILITY">{`Restaurants may change the availability of products or options at any time.

If a product is no longer available when the final order is created, Ponesi may prevent that product from being ordered.

The fact that a product was previously added to the cart does not guarantee that it will remain available.

Ponesi will seek to clearly inform the user of such changes.`}</S>

        <S n="10" title="RESTAURANT HOURS AND AVAILABILITY">{`A restaurant may be shown as open, closed, paused or temporarily unavailable for new orders.

Restaurants may also use Busy Mode, which can affect the earliest possible pickup time.

Ponesi does not guarantee that a restaurant will be able to accept a new order at all times.`}</S>

        <S n="11" title="CANCELLATION">{`The ability to cancel an order depends on the order status and whether the restaurant has already started preparation.

Legal exceptions to withdrawal rights may apply to goods prepared according to a specific customer request, goods with a short shelf life or goods liable to deteriorate rapidly.

Nothing in these Terms limits mandatory consumer rights under the laws of the Republic of Serbia.`}</S>

        <S n="12" title="ALLERGENS AND FOOD INFORMATION">{`Information about ingredients, allergens, food preparation and products displayed on Ponesi is provided by the relevant restaurants.

Users with allergies, intolerances or specific dietary requirements should contact the restaurant directly when they require additional confirmation regarding ingredients or possible cross-contamination.

Ponesi does not guarantee that a product is free from a particular allergen unless there is an appropriate factual and legal basis for such a guarantee.`}</S>

        <S n="13" title="RESTAURANT RESPONSIBILITY">{`The restaurant is responsible for its own offer and fulfillment of orders, including in particular:
- food quality;
- hygiene and food safety;
- quantity;
- preparation;
- ingredients;
- allergens;
- accuracy of information provided by the restaurant;
- product availability;
- fulfillment of accepted orders;
- obligations the restaurant has toward the consumer as a merchant.

This provision does not exclude Sahara Group DOO's responsibility for its own actions or obligations under applicable law.`}</S>

        <S n="14" title="COMPLAINTS AND SUPPORT">{`For issues relating to food itself, quantity, preparation or the service of a specific restaurant, users may contact that restaurant.

For technical issues relating to the Ponesi platform, users may contact Sahara Group DOO at:

privacy@ponesi.rs

Where necessary and legally permitted, Ponesi may assist in exchanging relevant information between the customer and restaurant for the purpose of resolving an issue.`}</S>

        <S n="15" title="PROHIBITED USE">{`Users may not:
- create fraudulent or malicious orders;
- attempt to access another person's account;
- attempt to access another restaurant's or user's data without authorization;
- manipulate prices or server requests;
- interfere with the operation of the Platform;
- abuse automated systems;
- use Ponesi contrary to applicable law.

Ponesi may temporarily or permanently restrict access for users who misuse the Platform.`}</S>

        <S n="16" title="PLATFORM AVAILABILITY">{`Ponesi seeks to provide a reliable and available Platform.

However, uninterrupted operation without technical interruptions cannot be guaranteed.

Functionality may be affected by maintenance, internet connectivity, infrastructure, third-party service providers or other technical events.`}</S>

        <S n="17" title="INTELLECTUAL PROPERTY">{`The Ponesi name, logo, design, software and other content owned by Sahara Group DOO or its licensors are protected by applicable intellectual property rights.

Content uploaded by restaurants remains subject to the rights of the relevant restaurant or other rights holder.`}</S>

        <S n="18" title="CHANGES TO THESE TERMS">{`Ponesi may amend these Terms from time to time to reflect Platform development, changes in the business model or legal requirements.

For material changes, users may be notified and may be required to accept the new version before placing another order.`}</S>

        <S n="19" title="GOVERNING LAW">{`These Terms are governed by the laws of the Republic of Serbia.

Mandatory consumer rights cannot be excluded by these Terms.

Any disputes are subject to the jurisdiction rules provided by applicable law in the Republic of Serbia.`}</S>

        <S n="20" title="CONTACT">{`Sahara Group DOO
Masarikov Trg 18
16000 Leskovac
Republic of Serbia
Tax ID (PIB): 115572837
Company Registration Number: 22177460
Email:
privacy@ponesi.rs`}</S>

        <div className="mt-10 flex gap-3 text-sm">
          <a href="/en/privacy" className="text-brand-red font-semibold underline" data-testid="link-to-privacy-en">Privacy Policy</a>
          <a href="/en" className="text-slate-500 font-semibold">Back to Ponesi</a>
        </div>
      </div>
    </div>
  );
}
